import React from "react";
import { type EditorState } from "react-draft-wysiwyg";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Button from "../../Buttons/Button";
import MediaSelector, { type MediaItem } from "../../Media/Selector/MediaSelector";
import { MediaType } from "../../Media/Selector/MediaUtilities";
import RetractableInput from "../RetractableInput";

type Props = {
    expanded: boolean;
    onExpandEvent: (e: React.MouseEvent<HTMLElement>) => void;
    onChange: (url: string, height: string, width: string, alt: string) => void;
    currentState: EditorState;
};

type State = {
    configure: boolean;
    width: string;
    height: string;
    flipped: boolean;
};

const MEDIA_FILTERS: Array<MediaType> = [ MediaType.Image ];

export default class ImageModal extends React.Component<Props, State> {
    public buttonRef: React.RefObject<HTMLDivElement | null> = React.createRef();

    public state: State = {
        configure: false,
        width    : "",
        height   : "",
        flipped  : false,
    };

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(!prevProps.expanded && this.props.expanded) {
            this.measureFlipped();
        }
    }

    public measureFlipped = (): void => {
        if(!this.buttonRef.current) {
            return;
        }

        if(!window) {
            return;
        }

        const bounding = this.buttonRef.current.getBoundingClientRect();
        const halfWidth = window.innerWidth / 2;

        this.setState({
            flipped: bounding.left > halfWidth,
        });
    };

    public stopPropagation = (e: React.MouseEvent<HTMLDivElement>): void => {
        e.stopPropagation();
    };

    public onChange = (values: Array<MediaItem>): void => {
        const { url: image } = values[0];

        if(image) {
            let { height, width } = this.state;

            if(!isNaN(parseFloat(height))) {
                height += "px";
            } else {
                height = "";
            }

            if(!isNaN(parseFloat(width))) {
                width += "px";
            } else {
                width = "";
            }

            this.setState({
                width    : "",
                height   : "",
                configure: false,
            });

            this.props.onChange(image, height, width, image);
        }
    };

    public onConfigure = (): void => {
        this.setState(prevState => ({
            configure: !prevState.configure,
        }));
    };

    public onConfigureChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.currentTarget;

        this.setState(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    public renderModal = (): React.ReactNode => {
        let configure;

        if(this.state.configure) {
            configure = (
                <div className="image-modal-configure">
                    <div className="media-small-buttons">
                        <div className="image-modal-configure-heading">
                            Configure
                        </div>
                        <div className="configure-button">
                            <Button onClick={this.onConfigure}>
                                <FontAwesomeLight icon={"cog"} />
                            </Button>
                        </div>
                    </div>
                    <div className="image-modal-configure-form">
                        <RetractableInput
                            label="Width (px)"
                            hasValue={!!this.state.width}
                        >
                            {(onFocus, onBlur) => (
                                <input
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                    type="number"
                                    min="1"
                                    name="width"
                                    value={this.state.width}
                                    onChange={this.onConfigureChange}
                                />
                            )}
                        </RetractableInput>
                        <RetractableInput
                            label="Height (px)"
                            hasValue={!!this.state.height}
                        >
                            {(onFocus, onBlur) => (
                                <input
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                    type="number"
                                    min="1"
                                    name="height"
                                    value={this.state.height}
                                    onChange={this.onConfigureChange}
                                />
                            )}
                        </RetractableInput>
                    </div>
                    <div>

                    </div>
                </div>
            );
        }

        const classes = [ "image-modal-wrapper" ];

        if(this.state.flipped) {
            classes.push("flipped");
        }

        return (
            <div
                className={classes.join(" ")}
                onClick={this.stopPropagation}
            >
                {configure}
                <MediaSelector
                    small
                    fullscreen={false}
                    onConfigure={this.onConfigure}
                    onChange={this.onChange}
                    filters={MEDIA_FILTERS}
                    disablePortal
                />
            </div>
        );
    };

    public render(): React.ReactNode {
        const { expanded, onExpandEvent } = this.props;

        return (
            <div
                ref={this.buttonRef}
                aria-haspopup="true"
                aria-expanded={expanded}
                aria-label="media-selector"
                className="image-modal"
            >
                <div
                    onClick={onExpandEvent}
                    className="rdw-option-wrapper"
                >
                    <FontAwesomeLight icon="image" />
                </div>
                {expanded ? this.renderModal() : undefined}
            </div>
        );
    }
}
